// Every checksum here is written once and read both ways: the generator asks for the digit that makes a number
// valid, and the checker asks whether the digit that is there is that one. A page that generated Luhn-valid cards
// with one routine and checked them with another would be two routines that quietly stop agreeing.

// What a failed check can say beyond "no": the digit that would have made it hold, which is what turns a checker
// into something that finds the typo rather than only reporting one.
export interface Check {
  valid: boolean;
  // The value with its separators taken out, which is what was actually checked.
  normalised: string;
  // The check digits as the value carries them: an IBAN's are its third and fourth characters, every other one's last.
  found: string;
  // Absent when the fault is the shape rather than the digit, since then there is no single digit to name.
  expected?: string;
  // What the number turned out to be, where the format carries more than a checksum: an issuer, a country, a group.
  detail?: string;
}

// Luhn, ISO/IEC 7812: double every second digit from the right, cast the tens out, and the total is a multiple of 10.
export function luhnDigit(body: string): string {
  return String((10 - luhnSum(`${body}0`) % 10) % 10);
}

export function luhnHolds(value: string): boolean {
  return value.length > 1 && luhnSum(value) % 10 === 0;
}

function luhnSum(digits: string): number {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    // Counted from the right, so which digits double does not depend on how long the number is.
    const fromRight = digits.length - 1 - i;
    const digit = digits.charCodeAt(i) - 48;
    if (fromRight % 2 === 1) {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    } else {
      sum += digit;
    }
  }
  return sum;
}

export function checkCard(value: string): Check | null {
  const normalised = value.replace(/[\s-]/g, "");
  // A length no issuer has ever used is not a card at all, rather than a card that fails: without this a thirteen
  // digit ISBN comes back as a payment card whose checksum does not hold, which is a true sentence about a number
  // nobody was claiming was a card, and `identify` is meant to offer what a value could be and not what it could not.
  if (!/^\d+$/.test(normalised) || !CARD_LENGTHS.has(normalised.length)) return null;

  const brand = cardBrandOf(normalised);
  const body = normalised.slice(0, -1);
  const expected = luhnDigit(body);
  return {
    valid: luhnHolds(normalised) && brand !== null,
    normalised,
    found: normalised.slice(-1),
    expected: luhnHolds(normalised) ? undefined : expected,
    // A number whose Luhn holds but whose prefix belongs to nobody is worth telling apart from one that is simply wrong.
    detail: brand ? brand.label : "No issuer claims this prefix and length",
  };
}

// ISO 7064 mod 97-10: move the first four characters to the end, letters become their position plus nine, and the
// remainder of the whole number by 97 is 1. The number is far past a double, so it is reduced as it is read.
export function ibanRemainder(rearranged: string): number {
  let remainder = 0;
  for (const character of rearranged) {
    const code = character.charCodeAt(0);
    const part = code >= 65 && code <= 90 ? String(code - 55) : character;
    for (const digit of part) remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
  }
  return remainder;
}

// The two digits that make an IBAN hold, worked out with `00` standing in for them where they will sit.
export function ibanCheckDigits(country: string, bban: string): string {
  return String(98 - ibanRemainder(`${bban}${country}00`)).padStart(2, "0");
}

export function checkIban(value: string): Check | null {
  const normalised = value.replace(/[\s-]/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(normalised)) return null;

  const country = normalised.slice(0, 2);
  const expected = IBAN_COUNTRIES[country];
  const bban = normalised.slice(4);
  const holds = ibanRemainder(`${bban}${normalised.slice(0, 4)}`) === 1;
  // Right after the country code rather than at the end, which is where every other format here keeps its digit.
  const found = normalised.slice(2, 4);

  // A length the country does not use is a different fault from a check digit that does not hold, and saying which
  // is the difference between "retype it" and "this is not a German account".
  if (expected && normalised.length !== expected.length) {
    return {
      valid: false,
      normalised,
      found,
      detail: `${expected.name} IBANs are ${expected.length} characters, and this is ${normalised.length}`,
    };
  }

  return {
    valid: holds && expected !== undefined,
    normalised,
    found,
    expected: holds ? undefined : ibanCheckDigits(country, bban),
    detail: expected ? expected.name : `No country uses the code ${country}`,
  };
}

// ISBN-10, ISO 2108: the ten digits weighted 10 down to 1 sum to a multiple of 11, and 10 is written X.
export function isbn10Digit(body: string): string {
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += (body.charCodeAt(i) - 48) * (10 - i);
  const digit = (11 - sum % 11) % 11;
  return digit === 10 ? "X" : String(digit);
}

// ISBN-13 is an EAN-13 and shares its checksum: alternating weights of 1 and 3, cast to the next multiple of ten.
export function eanDigit(body: string): string {
  let sum = 0;
  for (let i = 0; i < body.length; i++) sum += (body.charCodeAt(i) - 48) * (i % 2 === 0 ? 1 : 3);
  return String((10 - sum % 10) % 10);
}

export function checkIsbn(value: string): Check | null {
  const normalised = value.replace(/[\s-]/g, "").toUpperCase();

  if (/^\d{9}[\dX]$/.test(normalised)) {
    const expected = isbn10Digit(normalised);
    return {
      valid: normalised[9] === expected,
      normalised,
      found: normalised[9],
      expected: normalised[9] === expected ? undefined : expected,
      detail: `ISBN-10, registration group ${normalised[0]}`,
    };
  }

  if (/^97[89]\d{10}$/.test(normalised)) {
    const expected = eanDigit(normalised.slice(0, 12));
    return {
      valid: normalised[12] === expected,
      normalised,
      found: normalised[12],
      expected: normalised[12] === expected ? undefined : expected,
      detail: `ISBN-13, prefix ${normalised.slice(0, 3)}`,
    };
  }

  return null;
}

export function checkEan(value: string): Check | null {
  const normalised = value.replace(/[\s-]/g, "");
  const kind = EAN_LENGTHS[normalised.length];
  if (!kind || !/^\d+$/.test(normalised)) return null;

  const expected = eanDigit(normalised.slice(0, -1));
  const found = normalised.slice(-1);
  return {
    valid: found === expected,
    normalised,
    found,
    expected: found === expected ? undefined : expected,
    detail: kind,
  };
}

// An IMEI is fifteen digits under Luhn, which is the same arithmetic as a card and a different thing entirely, so it
// is named rather than left to be reported as a card number nobody issued.
export function checkImei(value: string): Check | null {
  const normalised = value.replace(/[\s-]/g, "");
  if (!/^\d{15}$/.test(normalised)) return null;

  const expected = luhnDigit(normalised.slice(0, 14));
  return {
    valid: luhnHolds(normalised),
    normalised,
    found: normalised.slice(-1),
    expected: luhnHolds(normalised) ? undefined : expected,
    detail: `Reporting body identifier ${normalised.slice(0, 2)}`,
  };
}

export interface Candidate extends Check {
  format: string;
}

// Every format the value could be, rather than the one it most looks like: fifteen digits are an Amex card and an
// IMEI both, and a page that picked one of them for the reader would be right half the time. Each checker answers
// null for a value that is not its shape at all, so what is left is the list worth showing.
export function identify(value: string): Candidate[] {
  return CHECKERS.flatMap(({ format, check }) => {
    const result = check(value);
    return result ? [{ format, ...result }] : [];
  });
}

// What the Check box offers before anything is typed and so the first thing tried: the Visa test number, which holds.
export const EXAMPLE_NUMBER = "4111 1111 1111 1111";

const CHECKERS: { format: string; check: (value: string) => Check | null }[] = [
  { format: "Payment card", check: checkCard },
  { format: "IBAN", check: checkIban },
  { format: "ISBN", check: checkIsbn },
  { format: "Barcode", check: checkEan },
  { format: "IMEI", check: checkImei },
];

export function cardBrandOf(digits: string): CardBrand | null {
  return CARD_BRANDS.find((brand) =>
    brand.lengths.includes(digits.length) && brand.prefixes.some((prefix) => digits.startsWith(prefix))
  ) ?? null;
}

export interface CardBrand {
  label: string;
  // Spelled out rather than written as ranges: a range is a second notation to get wrong, and the list is short.
  prefixes: string[];
  lengths: number[];
}

// The issuer identification numbers a test card is worth wearing. Every one of these is a published IIN range and
// none of them reaches an account: what makes a number a test number is that the batch is generated, not the prefix.
export const CARD_BRANDS: CardBrand[] = [
  { label: "Visa", prefixes: ["4"], lengths: [16] },
  {
    label: "Mastercard",
    prefixes: ["51", "52", "53", "54", "55", "2221", "2222", "2223", "2224", "2225", "2226", "2270", "2720"],
    lengths: [16],
  },
  { label: "American Express", prefixes: ["34", "37"], lengths: [15] },
  { label: "Discover", prefixes: ["6011", "644", "645", "646", "647", "648", "649", "65"], lengths: [16] },
  { label: "JCB", prefixes: ["3528", "3529", "353", "354", "355", "356", "357", "358"], lengths: [16] },
  { label: "Diners Club", prefixes: ["300", "301", "302", "303", "304", "305", "36", "38"], lengths: [14] },
  { label: "UnionPay", prefixes: ["62"], lengths: [16, 19] },
  { label: "Maestro", prefixes: ["5018", "5020", "5038", "6304", "6759", "6761", "6762", "6763"], lengths: [16] },
];

const CARD_LENGTHS = new Set(CARD_BRANDS.flatMap((brand) => brand.lengths));

export interface IbanCountry {
  name: string;
  length: number;
  // The BBAN's shape: `n` a digit, `a` an upper-case letter, `c` either, each followed by how many of them.
  bban: string;
}

// The registry is a hundred-odd countries and this is the dozen the locales here can mean, plus the two anybody
// testing European payments reaches for. A country not listed is reported as one rather than guessed at.
export const IBAN_COUNTRIES: Record<string, IbanCountry> = {
  DE: { name: "Germany", length: 22, bban: "n18" },
  GB: { name: "United Kingdom", length: 22, bban: "a4n14" },
  FR: { name: "France", length: 27, bban: "n10c11n2" },
  ES: { name: "Spain", length: 24, bban: "n20" },
  IT: { name: "Italy", length: 27, bban: "a1n10c12" },
  NL: { name: "Netherlands", length: 18, bban: "a4n10" },
  BE: { name: "Belgium", length: 16, bban: "n12" },
  AT: { name: "Austria", length: 20, bban: "n16" },
  CH: { name: "Switzerland", length: 21, bban: "n5c12" },
  IE: { name: "Ireland", length: 22, bban: "a4n14" },
  PT: { name: "Portugal", length: 25, bban: "n21" },
  PL: { name: "Poland", length: 28, bban: "n24" },
  SE: { name: "Sweden", length: 24, bban: "n20" },
  NO: { name: "Norway", length: 15, bban: "n11" },
  DK: { name: "Denmark", length: 18, bban: "n14" },
  FI: { name: "Finland", length: 18, bban: "n14" },
};

// UPC-A is a twelve-digit EAN with a leading zero implied, and both are checked by the same alternating weights.
const EAN_LENGTHS: Record<number, string> = { 8: "EAN-8", 12: "UPC-A", 13: "EAN-13", 14: "GTIN-14" };
